const GOOGLE_FONTS_CSS2 = 'https://fonts.googleapis.com/css2';

/** One `@font-face` of a Google Fonts CSS2 answer: a file and the characters it covers. */
interface FontFaceSource {
  url: string;
  unicodeRange?: string;
}

const loaded = new Map<string, Promise<string | null>>();

function parseFontFaces(css: string): FontFaceSource[] {
  return css
    .split('@font-face')
    .slice(1)
    .map((block) => ({
      url: /url\(([^)]+)\)/.exec(block)?.[1]?.replace(/['"]/g, '') ?? '',
      unicodeRange: /unicode-range:\s*([^;}]+)/.exec(block)?.[1]?.trim(),
    }))
    .filter((face) => face.url);
}

/** The family name the tabular copy is registered under. */
export const tabularFamilyName = (family: string) => `${family} Tabular`;

/**
 * Registers a copy of a Google font with tabular figures switched on, for canvas text: canvas has no
 * `font-variant-numeric`, and Unbounded's tabular digits are their own narrower glyphs, not the same ones re-spaced.
 * `FontFace.featureSettings` (MDN; Chrome 140+, Firefox 41+) bakes `tnum` into the face itself.
 *
 * Resolves the family name to draw with, or `null` when the font did not load — the caller falls back to the
 * proportional face. Loaded once per family and weight.
 */
export function loadTabularFont(family: string, weight: number, fontSet: FontFaceSet = document.fonts): Promise<string | null> {
  const key = `${family}:${weight}`;
  const cached = loaded.get(key);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const url = `${GOOGLE_FONTS_CSS2}?family=${family.trim().replace(/ /g, '+')}:wght@${weight}&display=swap`;
      const response = await fetch(url);
      if (!response.ok) return null;
      const name = tabularFamilyName(family);
      const faces = parseFontFaces(await response.text()).map(
        (source) =>
          new FontFace(name, `url(${source.url}) format('woff2')`, {
            weight: String(weight),
            unicodeRange: source.unicodeRange,
            featureSettings: '"tnum" 1',
          }),
      );
      if (faces.length === 0) return null;
      await Promise.all(faces.map((face) => face.load()));
      faces.forEach((face) => fontSet.add(face));
      return name;
    } catch {
      return null;
    }
  })();
  loaded.set(key, promise);
  // A failure is not cached: the next review may be built when the network is back.
  promise.then((name) => {
    if (!name) loaded.delete(key);
  });
  return promise;
}
